/* ═══════════════════════════════════════════════════════════════
   API Client — HTTP calls to SpecForma EC2 API
   JWT-only. No legacy API-key fallback (removed 2026-04-18; the old
   key was extractable from the installer bundle).

   Two protections live here:

     1. In-flight request dedup. When two callers fire the same body
        at the same time (e.g. five dashboard widgets all asking for
        `listEntries(projectId)` on mount), the first wins and the
        rest share the promise. Cuts duplicate-burst traffic by ~70%
        without any caching semantics changing.

     2. 429 backoff + retry-once. If the server says "slow down"
        we sleep briefly and retry exactly one more time before
        bubbling the error to the caller. Users never see a "rate
        limit" toast for ordinary traffic — only sustained attacks
        or true runaway loops reach the second 429.
   ═══════════════════════════════════════════════════════════════ */

import { getAuthToken } from './authService';

import { API_URL } from '../lib/apiBase';

// Reads (action=list/query) are dedupable: same input = same output, so
// concurrent identical reads can share one in-flight promise.  Writes
// (save/remove/update) are NOT dedupable — even if the body is identical,
// the second caller still expects an "I made the change" semantic.
const READ_ACTIONS = new Set(['list', 'query']);

const inflight = new Map<string, Promise<any>>();

function dedupeKey(body: Record<string, any>): string {
  // Stable JSON — Object.keys order matters; sort to keep the key consistent
  // regardless of caller's property order.
  return JSON.stringify(body, Object.keys(body).sort());
}

function sleep(ms: number): Promise<void> {
  return new Promise((r) => setTimeout(r, ms));
}

/** Error thrown for a non-2xx /api response. `failedOp` = index of the batch op that failed. */
export type ApiError = Error & { reason?: string; status?: number; failedOp?: number };

async function apiCall(body: Record<string, any>, attempt = 0): Promise<any> {
  const token = getAuthToken();
  if (!token) {
    throw new Error('Not signed in');
  }

  // Dedup: only for safe READ requests, and only on the first attempt.
  // On retries we always fire a fresh request — the original promise has
  // already failed with 429 and isn't reusable.
  const isRead = READ_ACTIONS.has(body.action);
  if (isRead && attempt === 0) {
    const key = dedupeKey(body);
    const existing = inflight.get(key);
    if (existing) return existing;

    const promise = doFetch(body, attempt).finally(() => {
      // Clear the slot once the network round-trip resolves so the next
      // request fires fresh. We don't keep results cached here — that's
      // cloudCache.ts's job; this layer is purely about collapsing
      // simultaneous in-flight calls.
      inflight.delete(key);
    });
    inflight.set(key, promise);
    return promise;
  }

  return doFetch(body, attempt);
}

async function doFetch(body: Record<string, any>, attempt: number): Promise<any> {
  const token = getAuthToken();
  if (!token) throw new Error('Not signed in');

  const payload = JSON.stringify(body);
  const response = await fetch(API_URL, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: payload,
  });

  // 413 — the request body is over the server's cap. Say how big it was
  // rather than surfacing a bare status code.
  if (response.status === 413) {
    const mb = (new TextEncoder().encode(payload).length / (1024 * 1024)).toFixed(1);
    const tooLarge = new Error(`Spec too large to save (${mb} MB)`) as Error & { status?: number };
    tooLarge.status = 413;
    throw tooLarge;
  }

  // 429 Too Many Requests — backoff and retry exactly once. The server's
  // express-rate-limit middleware uses a fixed 60s window; if we got
  // bucketed at second 59 we can just wait a beat and the bucket resets.
  if (response.status === 429 && attempt < 1) {
    await sleep(800 + Math.random() * 600); // 0.8–1.4s jitter
    return apiCall(body, attempt + 1);
  }

  if (!response.ok) {
    const err = await response.json().catch(() => ({ error: response.statusText }));
    // Servers on this endpoint attach a specific `reason` alongside the
    // generic `error` (e.g. "Permission denied" + "Missing team_members.
    // manage_roles..."). Previously only `error` was surfaced, so every
    // 403 read as an unhelpful "Permission denied" no matter why. Fold the
    // reason into the message (still just a string, so every existing
    // catch-and-toast caller gets more detail for free) and also attach it
    // as a `.reason` property for callers that want it structured.
    // Batch failures return { error, failedOp, message }: `message` is the
    // specific cause, so it wins over the generic `error`.
    const message = err.message
      ? err.message
      : err.reason
        ? `${err.error || 'Request failed'}: ${err.reason}`
        : err.error || `API error: ${response.status}`;
    const apiError = new Error(message) as ApiError;
    apiError.reason = err.reason;
    apiError.status = response.status;
    if (typeof err.failedOp === 'number') apiError.failedOp = err.failedOp;
    throw apiError;
  }

  return response.json();
}

export async function dbList(
  table: string,
  firmId: string | null,
  filters?: Record<string, any>,
  orderBy?: string,
  columns?: string[]
): Promise<any[]> {
  return apiCall({ action: 'list', table, firmId, filters, orderBy, columns });
}

export async function dbQuery(
  table: string,
  firmId: string | null,
  filters?: { col: string; op: string; val: any }[],
  orderBy?: string
): Promise<any[]> {
  return apiCall({ action: 'query', table, firmId, filters, orderBy });
}

export async function dbSave(
  table: string,
  record: Record<string, any>
): Promise<Record<string, any>> {
  return apiCall({ action: 'save', table, record });
}

/** HARD delete. For `table: 'team_members'` the server requires an explicit
 *  `confirmHardDelete: true` intent flag (Team Compliance Sprint, Batch E) —
 *  pass `opts.confirmHardDelete` to supply it; every other table ignores the
 *  flag. Soft-deactivating a team member is a SEPARATE action — see
 *  `dbDeactivate` below — never inferred from calling this. */
export async function dbRemove(
  table: string,
  id: string,
  opts?: { confirmHardDelete?: boolean }
): Promise<any> {
  return apiCall({
    action: 'remove',
    table,
    id,
    ...(opts?.confirmHardDelete ? { confirmHardDelete: true } : {}),
  });
}

/** Explicit soft-deactivate intent — currently team_members only (see
 *  ec2-api/server.js action:'deactivate'). Always sets active=false, never
 *  a row delete, regardless of the caller's permission tier — soft vs.
 *  hard is the CALLER's stated intent, not something the server infers.
 *  Returns `{ success, deactivated: true, member }`. */
export async function dbDeactivate(table: string, id: string): Promise<any> {
  return apiCall({ action: 'deactivate', table, id });
}

export async function dbUpdate(
  table: string,
  updates: Record<string, any>,
  filters: { col: string; op: string; val: any }[]
): Promise<any[]> {
  return apiCall({ action: 'update', table, updates, filters });
}

/** Atomic single-cell upsert for hour_forecasts.manual rows. Backed by the
 *  partial unique index uq_hour_forecasts_manual_cell. Pass hours = 0 to
 *  delete the cell (idempotent). phase_id is stamped server-side by the
 *  stamp_phase_from_project trigger (migration 017) — clients do not pick
 *  the phase. Returns the resulting row, or {deleted, ids}. */
export async function forecastUpsertCell(args: {
  teamMemberId: string;
  projectId: string | null;
  dayDate: string;
  hours: number;
  notes?: string | null;
}): Promise<any> {
  return apiCall({ action: 'forecast_upsert_cell', ...args });
}

/** Tranche 3 — Forecasting Meeting mode: stamps "Time's good" for a member's
 *  whole week. Mirrors forecastUpsertCell's pattern — gated server-side by
 *  the same forecasting.edit_* ladder, CALLER stamped as confirmed_by
 *  (never a client-supplied id). Idempotent — re-confirming just refreshes
 *  the timestamp/confirmer (backed by uq_forecast_confirms_member_week). */
export async function forecastConfirmWeek(args: {
  teamMemberId: string;
  weekStart: string;
}): Promise<any> {
  return apiCall({ action: 'forecast_confirm_week', ...args });
}

/* ─── Master → variant update feed (Phase 3) ───
   Publishing raises the rows; accepting or rejecting is a decision, not a
   table write — the server applies the feed stamp AND the client's layer in
   one transaction, gated on specs.accept_updates. */

/* ─── Batch writes: N ops, ONE server transaction (ec2-api/server.js runBatch) ───
   Every op is its single-action twin's body (same permission gate, same firm
   scoping). Nothing lands unless everything lands; a failure throws an
   ApiError whose `failedOp` is the 0-based index of the op that broke. */
export type BatchOp =
  | { action: 'save'; table: string; record: Record<string, unknown> }
  | {
      action: 'update';
      table: string;
      updates: Record<string, unknown>;
      filters: { col: string; op: string; val: unknown }[];
    }
  | { action: 'remove'; table: string; id: string }
  | { action: 'variant_changes_create'; entries: unknown[] }
  | { action: 'module_update_raise'; [key: string]: unknown };

export interface BatchResponse {
  results: unknown[];
}

export async function dbBatch(ops: BatchOp[]): Promise<BatchResponse> {
  return apiCall({ action: 'batch', ops });
}

export async function variantChangesCreate(entries: unknown[]): Promise<any> {
  return apiCall({ action: 'variant_changes_create', entries });
}

/** Phase 6 — a module definition moved, so every non-project copy of it gets
 *  a PR. Gated on modules.edit; the server does the scan (masters and their
 *  layers ONLY — a project spec is never a target). */
export async function moduleUpdateRaise(args: {
  moduleId: string;
  moduleName: string;
  fromVersion: number;
  toVersion: number;
  defDiff: unknown;
}): Promise<any> {
  return apiCall({ action: 'module_update_raise', ...args });
}

export async function variantChangeDecide(args: {
  entryId: string;
  changeId: string;
  decision: 'accepted' | 'rejected';
}): Promise<any> {
  return apiCall({ action: 'variant_change_decide', ...args });
}

export async function variantChangeDecideAll(args: {
  entryId: string;
  decision: 'accepted' | 'rejected';
}): Promise<any> {
  return apiCall({ action: 'variant_change_decide_all', ...args });
}

/* ─── SYNC — the Review feed into project copies (docs/SYNC_PLAN.md §2.2) ───
   Server-gated: list = project_specs.view; decide/finish = specs.publish
   (project spec) or formas.publish (project forma); project publish =
   specs.publish; module publish = modules.publish. Typed wrappers live in
   services/syncService.ts. */

export async function syncFeedList(args: {
  projectId?: string;
  targetKind?: 'project_spec' | 'project_forma';
  targetId?: string;
  includeResolved?: boolean;
  includeContent?: boolean;
}): Promise<any> {
  return apiCall({ action: 'sync_feed_list', ...args });
}

export async function syncReviewDecide(args: {
  rowId: string;
  decisions: Record<string, 'accept' | 'reject' | null>;
}): Promise<any> {
  return apiCall({ action: 'sync_review_decide', ...args });
}

export async function syncReviewFinish(args: {
  rowId: string;
  decisions: Record<string, 'accept' | 'reject' | null>;
  /** A project spec's blocks, or a project forma's whole forma. */
  content: unknown[] | Record<string, unknown>;
}): Promise<any> {
  return apiCall({ action: 'sync_review_finish', ...args });
}

export async function projectSpecPublish(projectSpecId: string): Promise<any> {
  return apiCall({ action: 'project_spec_publish', projectSpecId });
}

export async function modulePublish(args: { moduleId: string; message?: string }): Promise<any> {
  return apiCall({ action: 'module_publish', ...args });
}

/* ─── Tranche 0 item 2/7 — first-class status-transition actions ───
   Mirror forecastUpsertCell's pattern: not generic table CRUD, gated
   server-side by the specific permission for the transition, and the
   CALLER (never a client-supplied id) is stamped as performer. The
   generic dbSave/dbUpdate above now actively REJECT status changes on
   timesheets/expenses/invoices — these are the only way to move them. */

export async function timesheetApprove(id: string): Promise<any> {
  return apiCall({ action: 'timesheet_approve', id });
}
export async function timesheetReject(id: string, reason?: string): Promise<any> {
  return apiCall({ action: 'timesheet_reject', id, reason });
}
export async function timesheetLock(id: string): Promise<any> {
  return apiCall({ action: 'timesheet_lock', id });
}
export async function timesheetUnlock(id: string): Promise<any> {
  return apiCall({ action: 'timesheet_unlock', id });
}
export async function expenseApprove(ids: string[]): Promise<any> {
  return apiCall({ action: 'expense_approve', ids });
}
export async function expenseReject(ids: string[], reason?: string): Promise<any> {
  return apiCall({ action: 'expense_reject', ids, reason });
}
export async function invoiceSend(id: string): Promise<any> {
  return apiCall({ action: 'invoice_send', id });
}
export async function invoiceMarkPaid(id: string, amount: number): Promise<any> {
  return apiCall({ action: 'invoice_mark_paid', id, amount });
}
export async function invoiceVoid(id: string): Promise<any> {
  return apiCall({ action: 'invoice_void', id });
}
/** Item 5 — marks pulled hour_entries as billed so they can't double-bill
 *  onto a second invoice. Idempotent; only claims currently-unbilled rows. */
export async function invoiceStampEntries(invoiceId: string, hourEntryIds: string[]): Promise<any> {
  return apiCall({ action: 'invoice_stamp_entries', invoiceId, hourEntryIds });
}
